import { FC } from "react";

import { FRENCH_MONTHS, getItemName, teethToEuro } from "./common/constants";
import {
  StyledChartContainer,
  StyledExplanation,
  StyledSection,
  StyledTitle,
} from "./common/styles";
import {
  BuyerSegmentData,
  SpendingChartProps,
  SpendingDataPoint,
  TopItemData,
  TopSpenderData,
} from "./common/types";

import { BuyerSegmentation } from "./spending/BuyerSegmentation";
import { PurchaseTypeChart } from "./spending/PurchaseTypeChart";
import { SpendingEvolutionChart } from "./spending/SpendingEvolutionChart";
import { SpendingOverview } from "./spending/SpendingOverview";
import { TopItems } from "./spending/TopItems";
import { TopSpenders } from "./spending/TopSpenders";

export const SpendingChart: FC<SpendingChartProps> = ({ purchases, users }) => {
  if (!purchases || purchases.length === 0) {
    return (
      <StyledChartContainer>
        <StyledSection>
          <StyledTitle>Analyse des Dépenses</StyledTitle>
          <StyledExplanation>
            Aucune donnée d'achat disponible. Les achats incluent les avatars et
            badges achetés avec les "teeth" (monnaie virtuelle du jeu). 1€ =
            environ 10-15 teeth selon les packs.
          </StyledExplanation>
        </StyledSection>
      </StyledChartContainer>
    );
  }
  // DATA PROCESSING
  const purchaseData = purchases.map((purchase) => ({
    id: purchase.id,
    userId: purchase.userId,
    itemKey: purchase.itemKey,
    price: purchase.price,
    type: purchase.type,
    createdAt: new Date(purchase.createdAt),
    toothBefore: purchase.toothBefore,
    toothAfter: purchase.toothAfter,
  }));

  const userMap = new Map(users.map((u) => [u.id, u.username]));

  // Monthly data
  const spendingByMonth: { [key: string]: any } = {};
  purchaseData.forEach((purchase) => {
    const month = purchase.createdAt.toLocaleString("fr-FR", {
      month: "long",
      year: "numeric",
    });
    if (!spendingByMonth[month]) {
      spendingByMonth[month] = {
        total_spent: 0,
        unique_buyers: new Set(),
        total_purchases: 0,
        items_purchased: {},
        avatar_purchases: 0,
        badge_purchases: 0,
        total_spent_euro: 0,
      };
    }
    spendingByMonth[month].total_spent += purchase.price;
    spendingByMonth[month].total_spent_euro += teethToEuro(purchase.price);
    spendingByMonth[month].unique_buyers.add(purchase.userId);
    spendingByMonth[month].total_purchases += 1;
    if (purchase.type === "a") spendingByMonth[month].avatar_purchases += 1;
    else if (purchase.type === "b") spendingByMonth[month].badge_purchases += 1;
    const itemName = getItemName(purchase.itemKey, purchase.type);
    spendingByMonth[month].items_purchased[itemName] =
      (spendingByMonth[month].items_purchased[itemName] || 0) + 1;
  });

  const chartData: SpendingDataPoint[] = Object.entries(spendingByMonth)
    .map(([month, data]) => ({
      month,
      total_spent: data.total_spent,
      total_spent_euro: data.total_spent_euro,
      unique_buyers: data.unique_buyers.size,
      total_purchases: data.total_purchases,
      avg_spent_per_buyer:
        data.unique_buyers.size > 0
          ? data.total_spent / data.unique_buyers.size
          : 0,
      avg_spent_per_buyer_euro:
        data.unique_buyers.size > 0
          ? data.total_spent_euro / data.unique_buyers.size
          : 0,
      most_popular_item:
        Object.entries(data.items_purchased).sort(
          ([, a], [, b]) => (b as number) - (a as number)
        )[0]?.[0] || "Aucun",
      avatar_purchases: data.avatar_purchases,
      badge_purchases: data.badge_purchases,
    }))
    .sort((a, b) => {
      const aMonth = a.month.split(" ")[0];
      const bMonth = b.month.split(" ")[0];
      const aYear = a.month.split(" ")[1];
      const bYear = b.month.split(" ")[1];
      if (aYear !== bYear) return aYear.localeCompare(bYear);
      return (
        FRENCH_MONTHS.indexOf(aMonth as any) -
        FRENCH_MONTHS.indexOf(bMonth as any)
      );
    });

  // Top items
  const itemStats: {
    [key: string]: {
      itemKey: string;
      totalPurchases: number;
      totalRevenue: number;
      totalRevenueEuro: number;
      buyers: { [userId: string]: number };
      type: string;
    };
  } = {};
  purchaseData.forEach((purchase) => {
    const itemKey = purchase.itemKey;
    if (!itemStats[itemKey]) {
      itemStats[itemKey] = {
        itemKey: purchase.itemKey,
        totalPurchases: 0,
        totalRevenue: 0,
        totalRevenueEuro: 0,
        buyers: {},
        type: purchase.type,
      };
    }
    itemStats[itemKey].totalPurchases += 1;
    itemStats[itemKey].totalRevenue += purchase.price;
    itemStats[itemKey].totalRevenueEuro += teethToEuro(purchase.price);
    itemStats[itemKey].buyers[purchase.userId] =
      (itemStats[itemKey].buyers[purchase.userId] || 0) + 1;
  });

  const topItems: TopItemData[] = Object.values(itemStats)
    .map((item) => {
      const buyersDetails: { [username: string]: number } = {};
      for (const userId in item.buyers) {
        buyersDetails[userMap.get(userId) || "Inconnu"] = item.buyers[userId];
      }
      return {
        itemKey: item.itemKey,
        itemName: item.type === "a" ? "Avatar" : `Badge ${item.itemKey}`,
        totalPurchases: item.totalPurchases,
        totalRevenue: item.totalRevenue,
        totalRevenueEuro: item.totalRevenueEuro,
        uniqueBuyers: Object.keys(item.buyers).length,
        buyers: buyersDetails,
        type: item.type,
      };
    })
    .sort((a, b) => b.totalRevenue - a.totalRevenue)
    .slice(0, 20);

  // Buyer segmentation
  const buyerStats: { [userId: string]: number } = {};
  purchaseData.forEach((purchase) => {
    buyerStats[purchase.userId] =
      (buyerStats[purchase.userId] || 0) + purchase.price;
  });

  const buyerSegments: BuyerSegmentData[] = [
    {
      segment: "Gros dépensiers",
      count: 0,
      totalSpent: 0,
      avgSpent: 0,
      percentage: 0,
    },
    {
      segment: "Occasionnels",
      count: 0,
      totalSpent: 0,
      avgSpent: 0,
      percentage: 0,
    },
    {
      segment: "Non-dépensiers",
      count: 0,
      totalSpent: 0,
      avgSpent: 0,
      percentage: 0,
    },
  ];
  Object.values(buyerStats).forEach((spent) => {
    const spentEuro = teethToEuro(spent);
    if (spentEuro >= 50) {
      buyerSegments[0].count += 1;
      buyerSegments[0].totalSpent += spentEuro;
    } else if (spentEuro >= 10) {
      buyerSegments[1].count += 1;
      buyerSegments[1].totalSpent += spentEuro;
    }
  });
  buyerSegments[2].count = users.length - Object.keys(buyerStats).length;
  buyerSegments.forEach((segment) => {
    segment.avgSpent =
      segment.count > 0 ? segment.totalSpent / segment.count : 0;
    segment.percentage =
      users.length > 0 ? (segment.count / users.length) * 100 : 0;
  });

  // Global metrics
  const totalRevenue = purchaseData.reduce((sum, p) => sum + p.price, 0);
  const overviewData = {
    totalRevenueEuro: teethToEuro(totalRevenue),
    uniqueBuyersCount: new Set(purchaseData.map((p) => p.userId)).size,
    avgSpentPerBuyerEuro: teethToEuro(
      new Set(purchaseData.map((p) => p.userId)).size > 0
        ? totalRevenue / new Set(purchaseData.map((p) => p.userId)).size
        : 0
    ),
    totalPurchases: purchaseData.length,
    avatarPurchases: purchaseData.filter((p) => p.type === "a").length,
    badgePurchases: purchaseData.filter((p) => p.type === "b").length,
  };

  // Top Spenders
  const spenders: { [userId: string]: TopSpenderData } = {};
  purchaseData.forEach((p) => {
    if (!spenders[p.userId]) {
      spenders[p.userId] = {
        userId: p.userId,
        username: userMap.get(p.userId) || "Inconnu",
        totalSpent: 0,
        totalSpentEuro: 0,
        purchaseCount: 0,
        purchases: [],
      };
    }
    spenders[p.userId].totalSpent += p.price;
    spenders[p.userId].purchaseCount += 1;
    spenders[p.userId].purchases.push({
      itemName: getItemName(p.itemKey, p.type),
      price: p.price,
      date: new Date(p.createdAt),
    });
  });
  const topSpenders: TopSpenderData[] = Object.values(spenders)
    .map((s) => ({ ...s, totalSpentEuro: teethToEuro(s.totalSpent) }))
    .sort((a, b) => b.totalSpent - a.totalSpent)
    .slice(0, 10);

  return (
    <StyledChartContainer>
      <StyledSection>
        <StyledTitle>Analyse des Dépenses - Vue d'ensemble</StyledTitle>
        <StyledExplanation>
          <strong>Conversion teeth/euros :</strong> 1€ = 10-15 teeth.
          <br />
          <strong>Avatars :</strong> Personnalisation (100-390 teeth).
          <br />
          <strong>Badges :</strong> Insignes de prestige (180 teeth).
        </StyledExplanation>
        <SpendingOverview data={overviewData} />
      </StyledSection>

      <StyledSection>
        <StyledTitle>Évolution des Dépenses par Mois</StyledTitle>
        <SpendingEvolutionChart data={chartData} />
      </StyledSection>

      <StyledSection>
        <StyledTitle>Répartition des Achats (Avatars vs Badges)</StyledTitle>
        <PurchaseTypeChart data={chartData} />
      </StyledSection>

      <StyledSection>
        <StyledTitle>Top 20 des Items les Plus Vendus</StyledTitle>
        <TopItems data={topItems} />
      </StyledSection>

      <StyledSection>
        <StyledTitle>Segmentation des Acheteurs</StyledTitle>
        <BuyerSegmentation data={buyerSegments} />
      </StyledSection>

      <StyledSection>
        <StyledTitle>Top 10 des Plus Gros Dépensiers</StyledTitle>
        <TopSpenders data={topSpenders} />
      </StyledSection>
    </StyledChartContainer>
  );
};

export default SpendingChart;
